import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../../config';
import {
  BuildingOffice2Icon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

/** Cuántas coincidencias se enseñan a la vez en el desplegable. */
const MAX_RESULTS = 8;

/**
 * Asigna a un usuario las empresas cuyos expedientes puede consultar.
 *
 * El flujo es buscar → añadir → ver la lista de añadidas, no un listado de todas
 * las empresas con casillas: con cientos de empresas eso sería inmanejable.
 *
 * Con una o más empresas asignadas, el usuario pasa a ser de SOLO LECTURA: ve
 * los expedientes de todas ellas acumulados, pero no puede crear ni modificar
 * nada (no habría forma de saber a qué empresa asignar lo que cree).
 *
 * Solo lo usa un admin. Ver docs/group-user-plan.md.
 */
const CompaniesManager = ({ userData, onUpdated }) => {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    setSelected((userData?.company_ids || []).map(String));
  }, [userData?.company_ids]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data } = await axios.get(`${baseAPI}/users/list?role=company`, {
          headers: authHeaders(),
        });
        // Una empresa no puede asignarse a sí misma.
        setCompanies(data.filter(c => c._id !== userData?._id));
      } catch (err) {
        // 404 = todavía no hay ninguna empresa dada de alta.
        if (err.response?.status !== 404) {
          toast.error('No se han podido cargar las empresas');
        }
        setCompanies([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, [userData?._id]);

  // Cerrar el desplegable al pinchar fuera.
  useEffect(() => {
    const onClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const label = company =>
    [company.username, company.userlastname].filter(Boolean).join(' ') ||
    company.usermail;

  const byId = useMemo(
    () => new Map(companies.map(c => [c._id, c])),
    [companies]
  );

  /** Empresas ya añadidas, en el orden en que se añadieron. */
  const selectedCompanies = useMemo(
    () => selected.map(id => byId.get(id)).filter(Boolean),
    [selected, byId]
  );

  /** Coincidencias del buscador, excluyendo las ya añadidas. */
  const results = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return [];
    return companies
      .filter(c => !selected.includes(c._id))
      .filter(c =>
        [c.username, c.userlastname, c.usermail]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(term)
      )
      .slice(0, MAX_RESULTS);
  }, [companies, selected, search]);

  const add = companyId => {
    setSelected(prev => (prev.includes(companyId) ? prev : [...prev, companyId]));
    setSearch('');
    setIsOpen(false);
  };

  const remove = companyId =>
    setSelected(prev => prev.filter(id => id !== companyId));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(
        `${baseAPI}/users/update/${userData._id}`,
        { company_ids: selected },
        { headers: authHeaders() }
      );
      toast.success(
        selected.length
          ? `Acceso guardado: ${selected.length} empresa${selected.length > 1 ? 's' : ''}`
          : 'El usuario vuelve a ver solo sus propios expedientes'
      );
      if (onUpdated) onUpdated(selected);
    } catch (err) {
      toast.error('No se ha podido guardar el acceso a las empresas');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='bg-white shadow-sm rounded-lg border border-slate-200 mt-6'>
      <div className='px-5 py-4 border-b border-slate-200'>
        <div className='flex items-center gap-2'>
          <BuildingOffice2Icon className='w-5 h-5 text-slate-500' />
          <h2 className='text-lg font-semibold text-slate-800'>
            Acceso a varias empresas
          </h2>
        </div>
        <p className='text-sm text-slate-500 mt-1'>
          Busca una empresa y añádela para que este usuario pueda consultar sus
          expedientes. Los verá todos juntos, y podrá filtrar por una empresa
          concreta.
        </p>
      </div>

      <div className='p-5'>
        {selected.length > 0 && (
          <div className='flex items-start gap-2 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
            <EyeIcon className='w-5 h-5 text-amber-600 shrink-0 mt-0.5' />
            <p className='text-sm text-amber-800'>
              Con empresas asignadas, este usuario pasa a ser de{' '}
              <strong>solo consulta</strong>: no podrá crear ni modificar
              expedientes, deudores ni anotaciones.
            </p>
          </div>
        )}

        {isLoading ? (
          <p className='text-sm text-slate-500'>Cargando empresas…</p>
        ) : (
          <>
            {/* Buscador con desplegable de coincidencias */}
            <div className='relative' ref={containerRef}>
              <MagnifyingGlassIcon className='w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2' />
              <input
                type='text'
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setIsOpen(true);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={e => {
                  if (e.key === 'Escape') setIsOpen(false);
                  if (e.key === 'Enter' && results.length === 1) {
                    e.preventDefault();
                    add(results[0]._id);
                  }
                }}
                placeholder='Buscar empresa por nombre o email…'
                className='w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500'
              />

              {isOpen && search.trim() && (
                <ul className='absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg divide-y divide-slate-100'>
                  {results.length === 0 ? (
                    <li className='px-3 py-2 text-sm text-slate-500'>
                      Ninguna empresa coincide con la búsqueda.
                    </li>
                  ) : (
                    results.map(company => (
                      <li key={company._id}>
                        <button
                          type='button'
                          onClick={() => add(company._id)}
                          className='w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-slate-50'
                        >
                          <PlusIcon className='w-4 h-4 text-indigo-600 shrink-0' />
                          <span className='min-w-0'>
                            <span className='block text-sm text-slate-800 truncate'>
                              {label(company)}
                            </span>
                            <span className='block text-xs text-slate-500 truncate'>
                              {company.usermail}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>

            {/* Empresas añadidas */}
            <div className='mt-4'>
              {selectedCompanies.length === 0 ? (
                <p className='text-sm text-slate-500'>
                  Todavía no has añadido ninguna empresa. Este usuario solo ve
                  sus propios expedientes.
                </p>
              ) : (
                <ul className='border border-slate-200 rounded-lg divide-y divide-slate-100'>
                  {selectedCompanies.map(company => (
                    <li
                      key={company._id}
                      className='flex items-center justify-between gap-3 px-3 py-2'
                    >
                      <span className='min-w-0'>
                        <span className='block text-sm text-slate-800 truncate'>
                          {label(company)}
                        </span>
                        <span className='block text-xs text-slate-500 truncate'>
                          {company.usermail}
                        </span>
                      </span>
                      <button
                        type='button'
                        onClick={() => remove(company._id)}
                        aria-label={`Quitar ${label(company)}`}
                        className='p-1 text-slate-400 hover:text-red-600 rounded-lg shrink-0'
                      >
                        <XMarkIcon className='w-5 h-5' />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className='flex items-center justify-between mt-4'>
              <span className='text-sm text-slate-500'>
                {selected.length} empresa{selected.length === 1 ? '' : 's'}{' '}
                añadida{selected.length === 1 ? '' : 's'}
              </span>
              <button
                type='button'
                onClick={handleSave}
                disabled={isSaving}
                className='px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50'
              >
                {isSaving ? 'Guardando…' : 'Guardar acceso'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompaniesManager;
